import React, { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Search, Bell, LogOut, ChevronDown, User, Wallet, CreditCard, Briefcase, UserRound, Menu, Settings2, Trash2, CheckCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { useNotifications } from "../context/NotificationsContext";
import { useI18n } from "../i18n";
import { api } from "../lib/api";
import { Input } from "./ui/input";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import CompanySelector from "./CompanySelector";
import LanguageSwitcher from "./LanguageSwitcher";

const MODULES = [
  { label: "nav_dashboard", path: "/dashboard" },
  { label: "nav_customers", path: "/customers" },
  { label: "nav_services", path: "/services" },
  { label: "nav_payments", path: "/payments" },
  { label: "nav_balance", path: "/balance" },
  { label: "nav_settings", path: "/settings" },
];

export default function TopHeader({ onMenuClick }) {
  const { user, logout } = useAuth();
  const { settings, formatCurrency } = useSettings();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState({ customers: [], services: [], payments: [] });
  const wrapRef = useRef(null);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  // Debounced global search
  useEffect(() => {
    if (!q || q.length < 2) { setResults({ customers: [], services: [], payments: [] }); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q } });
        setResults(data);
      } catch { setResults({ customers: [], services: [], payments: [] }); }
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  const moduleMatches = MODULES
    .map((r) => ({ ...r, name: t(r.label) }))
    .filter((r) => !q || r.name.toLowerCase().includes(q.toLowerCase()));

  const initials = (user?.name || user?.email || "A").slice(0, 2).toUpperCase();

  // AP-033 · Live notifications via global context (WebSocket + polling fallback).
  const { items: notifications, unread, connected, markRead, markAllRead, removeNotification } = useNotifications();

  const onNotifClick = async (n) => {
    if (!n.read) await markRead(n.id);
    if (n.link) { navigate(n.link); return; }
    if (n.ref_type === "payment") navigate("/payments");
    else if (n.ref_type === "process") navigate("/processes/list");
    else if (n.ref_type === "communication") navigate("/communication");
  };

  const totalResults = results.customers.length + results.services.length + results.payments.length + moduleMatches.length;

  return (
    <header
      data-testid="app-topheader"
      className="sticky top-0 z-30 h-16 flex items-center gap-2 sm:gap-4 px-3 sm:px-4 md:px-6 lg:px-8 border-b border-border bg-white/85 backdrop-blur-xl"
    >
      {/* AP-056 · Hamburger menu — only visible below lg (1024px) */}
      <button
        type="button"
        onClick={onMenuClick}
        aria-label="Abrir menu"
        data-testid="app-nav-toggle"
        className="lg:hidden inline-flex items-center justify-center h-9 w-9 rounded-md border border-border bg-white hover:bg-muted text-foreground"
      >
        <Menu className="h-5 w-5" />
      </button>
      {/* Logo (mobile) */}
      <div className="lg:hidden flex items-center gap-2 min-w-0">
        {settings?.company_logo ? (
          <img src={settings.company_logo} alt="logo" className="h-8 w-8 rounded-md object-cover" />
        ) : (
          <div className="h-8 w-8 rounded-md bg-primary/10 border border-primary/30 grid place-items-center">
            <Wallet className="h-4 w-4 text-primary" />
          </div>
        )}
        <span className="font-display font-bold text-sm">{settings?.company_name || "JMS ERP"}</span>
      </div>

      {/* Company badge (desktop) */}
      <div className="hidden lg:flex items-center gap-2 min-w-0">
        {settings?.company_logo ? (
          <img src={settings.company_logo} alt="logo" className="h-8 w-8 rounded-md object-cover border border-border" />
        ) : null}
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Empresa</p>
          <p className="text-sm font-medium truncate">{settings?.company_name || "JMS ERP"}</p>
        </div>
      </div>

      {/* Search — AP-021 Sprint 2 · Command palette trigger (Ctrl+K) */}
      <div className="relative flex-1 max-w-xl mx-auto">
        <button
          type="button"
          onClick={() => window.__openCmdK && window.__openCmdK()}
          data-testid="topheader-cmdk-trigger"
          className="w-full h-9 pl-9 pr-16 flex items-center rounded-md bg-muted/40 border border-transparent hover:border-border hover:bg-white transition-colors text-left text-sm text-muted-foreground"
        >
          <Search className="h-4 w-4 absolute left-3" />
          <span className="truncate">Buscar módulos, clientes, serviços…</span>
          <span className="absolute right-3 hidden sm:inline-flex items-center gap-1 text-[10px] text-muted-foreground/70">
            <kbd className="px-1.5 py-0.5 rounded border border-border bg-white font-mono">⌘K</kbd>
            <kbd className="px-1.5 py-0.5 rounded border border-border bg-white font-mono">Ctrl K</kbd>
          </span>
        </button>
      </div>

      {/* AP-049 · Company Selector — Master Super Admin only. Hidden for
          all other roles by the component itself. */}
      <CompanySelector />
      <LanguageSwitcher />

      {/* Notifications */}
      <Popover>
        <PopoverTrigger asChild>
          <button
            data-testid="notifications-button"
            className="relative h-9 w-9 rounded-md border border-border bg-white hover:bg-muted transition-colors grid place-items-center"
            aria-label="Notifications"
          >
            <Bell className="h-4 w-4 text-muted-foreground" />
            {unread > 0 && (
              <span className="absolute -top-1 -right-1 h-4 min-w-4 px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold grid place-items-center">
                {unread}
              </span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-96 p-0" data-testid="notifications-panel">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <div className="flex items-center gap-2">
              <p className="font-display font-semibold text-sm">Notificações</p>
              {connected && <span data-testid="notif-realtime-indicator" title="Tempo real conectado" className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />}
            </div>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <button data-testid="mark-all-read" onClick={() => markAllRead()}
                  className="text-xs text-primary hover:underline flex items-center gap-1">
                  <CheckCheck className="h-3.5 w-3.5" />Marcar todas
                </button>
              )}
              <Link to="/settings/notifications" data-testid="notif-settings-quick"
                className="p-1 rounded hover:bg-slate-100 text-slate-500" title="Preferências">
                <Settings2 className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 && (
              <p className="px-4 py-8 text-sm text-muted-foreground text-center">Tudo em dia ✓</p>
            )}
            {notifications.slice(0, 15).map((n) => (
              <div key={n.id} data-testid={`notif-${n.id}`}
                className={`group flex items-start gap-2 px-3 py-2.5 border-b border-border last:border-0 hover:bg-muted/60 transition-colors ${n.read ? "" : "bg-primary/5"}`}>
                <button onClick={() => onNotifClick(n)} className="flex-1 min-w-0 text-left flex items-start gap-2">
                  {!n.read && <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />}
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{n.title}</p>
                    {n.body && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.body}</p>}
                    <p className="text-[10px] text-slate-400 mt-0.5">{n.category || n.type}</p>
                  </div>
                </button>
                <button data-testid={`notif-del-${n.id}`} onClick={() => removeNotification(n.id)}
                  className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-rose-50 text-rose-500 transition"
                  title="Excluir">
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
          <Link to="/notifications" className="block px-4 py-2.5 text-center text-xs font-semibold text-primary hover:bg-primary/5 border-t border-border"
            data-testid="notif-open-center">
            Ver todas na Central →
          </Link>
        </PopoverContent>
      </Popover>

      {/* User Profile dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid="user-profile-button"
            className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-muted transition-colors"
          >
            <div className="h-8 w-8 rounded-md bg-primary/10 border border-primary/30 grid place-items-center text-primary font-semibold text-xs">
              {initials}
            </div>
            <div className="hidden sm:block text-left">
              <p className="text-sm font-medium leading-none">{user?.name || "Admin"}</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">{user?.role || ""}</p>
            </div>
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="font-normal">
            <div className="flex flex-col space-y-1">
              <p className="text-sm font-medium">{user?.name || "Admin"}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem data-testid="menu-profile" onSelect={() => navigate("/settings")}>
            <User className="h-4 w-4 mr-2" /> {t("user_profile")}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            data-testid="menu-logout"
            onSelect={handleLogout}
            className="text-destructive focus:text-destructive"
          >
            <LogOut className="h-4 w-4 mr-2" /> {t("logout")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
